import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { 
  ArrowsUpDownIcon, 
  EllipsisHorizontalIcon, 
} from '@heroicons/react/24/solid';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import { TableFooter } from '@/components/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useCampaigns } from '@/lib/hooks/useCampaign';
import { PageHeader } from '@/components/ui/page-header';
import type { Campaign } from '@/lib/services/campaigns';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

// Format numbers to display like in the mockup (100K, 10K, etc.)
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace('.0', '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + 'K';
  }
  return num.toString();
};

export default function CampaignsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);
  const { campaigns = [], createCampaign, deleteCampaign, toggleCampaignStatus, isLoading } = useCampaigns();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const checkoutStatus = searchParams.get('checkout');
    const upgradeStatus = searchParams.get('upgrade');

    if (checkoutStatus === 'success') {
      toast.success('Welcome! Your 14-day trial has started.');
      searchParams.delete('checkout');
      navigate({ search: searchParams.toString() }, { replace: true });
    } else if (upgradeStatus === 'success') {
      toast.success('Plan upgraded successfully!');
      searchParams.delete('upgrade');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [location.search, navigate]);

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCampaignName.trim()) return;

    setIsCreating(true);
    try {
      const newCampaign = await createCampaign({ name: newCampaignName.trim() });
      setNewCampaignName('');
      setIsCreateDialogOpen(false);
      
      // Navigate to the campaign editor
      navigate(`/dashboard/campaigns/${newCampaign.id}/edit`);
    } catch (error) {
      console.error('Error creating campaign:', error);
      // Note: Error toast is already handled in the useCampaigns hook
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleStatus = async (campaign: Campaign) => {
    try {
      await toggleCampaignStatus(campaign.id.toString(), campaign.status);
    } catch (error) {
      console.error('Error updating campaign:', error);
    }
  };

  // Check if campaign is ready to be launched
  const isCampaignReady = (campaign: Campaign): boolean => {
    // Check if sequence exists and has steps
    if (!campaign.sequence || !Array.isArray(campaign.sequence) || campaign.sequence.length === 0) {
      return false;
    }

    // Check if all steps have required fields
    const hasValidSteps = campaign.sequence.every((step) => {
      return (
        step.subject &&
        step.subject.trim() !== '' &&
        step.content &&
        step.content.trim() !== '' &&
        step.content !== '<p></p>' &&
        step.senderEmail &&
        step.senderEmail.trim() !== ''
      );
    });

    return hasValidSteps;
  };

  // Get tooltip message for why campaign isn't ready
  const getNotReadyMessage = (campaign: Campaign): string => {
    if (!campaign.sequence || !Array.isArray(campaign.sequence) || campaign.sequence.length === 0) {
      return 'Add an email sequence to launch this campaign';
    }

    const invalidSteps = campaign.sequence.filter((step) => {
      return (
        !step.subject ||
        step.subject.trim() === '' ||
        !step.content ||
        step.content.trim() === '' ||
        step.content === '<p></p>' ||
        !step.senderEmail ||
        step.senderEmail.trim() === ''
      );
    });

    if (invalidSteps.length > 0) {
      return 'Complete all email steps (subject, content, and sender) to launch';
    }

    return 'Campaign is not ready to launch';
  };

  const handleDeleteCampaign = async (campaignId: number) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await deleteCampaign(campaignId.toString());
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };


  const handleBulkDelete = async () => {
    if (selectedCampaignIds.length === 0) return;

    setIsDeleting(true);
    try {
      // Delete all selected campaigns
      await Promise.all(
        selectedCampaignIds.map((id) => deleteCampaign(id.toString()))
      );
      // Clear selection after successful deletion
      setRowSelection({});
      setIsDeleteDialogOpen(false);
      setSelectedCampaignIds([]);
      toast.success(`Successfully deleted ${selectedCampaignIds.length} campaign${selectedCampaignIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error deleting campaigns:', error);
      toast.error('Failed to delete some campaigns');
    } finally {
      setIsDeleting(false);
    }
  };

  // Column definitions
  const columns: ColumnDef<Campaign, any>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="!p-0 h-auto font-normal hover:text-foreground hover:bg-transparent"
          >
            Name
            <ArrowsUpDownIcon className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="font-normal truncate max-w-[300px]">
          {row.getValue('name')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const campaign = row.original;
        const isReady = isCampaignReady(campaign);
        const isActive = campaign.status === 'active';
        const canToggle = isActive || isReady; // Can toggle if active (to pause) or ready (to activate)

        const switchElement = (
          <Switch 
            checked={isActive}
            onCheckedChange={canToggle ? () => handleToggleStatus(campaign) : undefined}
            disabled={!canToggle}
            className={!canToggle ? 'opacity-50 cursor-not-allowed' : ''}
          />
        );

        if (!canToggle) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-block">
                  {switchElement}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getNotReadyMessage(campaign)}</p>
              </TooltipContent>
            </Tooltip>
          );
        }

        return switchElement;
      },
    },
    {
      accessorKey: 'leadsAdded',
      header: () => <div className="text-right">Leads Added</div>,
      cell: ({ row }) => {
        const leadsAdded = row.getValue('leadsAdded') as number;
        return <div className="text-right font-normal">{formatNumber(leadsAdded || 0)}</div>;
      },
    },
    {
      accessorKey: 'sent',
      header: () => <div className="text-right">Sent</div>,
      cell: ({ row }) => {
        const sent = row.getValue('sent') as number;
        return <div className="text-right font-normal">{formatNumber(sent || 0)}</div>;
      },
    },
    {
      accessorKey: 'clicked',
      header: () => <div className="text-right">Clicked</div>,
      cell: ({ row }) => {
        const clicked = row.getValue('clicked') as number;
        return <div className="text-right font-normal">{formatNumber(clicked || 0)}</div>;
      },
    },
    {
      accessorKey: 'replied',
      header: () => <div className="text-right">Replied</div>,
      cell: ({ row }) => {
        const replied = row.getValue('replied') as number;
        return <div className="text-right font-normal">{formatNumber(replied || 0)}</div>;
      },
    },
    {
      accessorKey: 'bounced',
      header: () => <div className="text-right">Bounced</div>,
      cell: ({ row }) => {
        const bounced = row.getValue('bounced') as number;
        return <div className="text-right font-normal">{formatNumber(bounced || 0)}</div>;
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      header: () => <div className="text-right mr-4">Actions</div>,
      cell: ({ row }) => {
        const campaign = row.original;
        return (
          <div className="flex justify-end mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="h-10 w-10 p-0 has-[>svg]:px-0 bg-card">
                  <span className="sr-only">Open menu</span>
                  <EllipsisHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/dashboard/campaigns/${campaign.id}/edit`)}>
                Edit campaign
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-danger-foreground"
                onClick={() => handleDeleteCampaign(campaign.id)}
              >
                Delete campaign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: campaigns,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Define handleOpenDeleteDialog after table is created
  const handleOpenDeleteDialog = () => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const ids = selectedRows.map(row => row.original.id);
    setSelectedCampaignIds(ids);
    setIsDeleteDialogOpen(true);
  };

  return (
    <>
      <PageHeader title="Campaigns" className="mt-page-top mb-header-gap">
        <Input
          placeholder="Search campaigns"
          value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('name')?.setFilterValue(event.target.value)
          }
          className="max-w-sm w-70"
          prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
        />
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="primary">
              Create a new campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader className="mt-2 mb-4">
              <DialogTitle>Create New Campaign</DialogTitle>
            </DialogHeader>

            {/* AI Setup option */}
            <div
              className="flex items-center gap-4 p-4 rounded-2xl border border-border bg-card hover:bg-accent cursor-pointer transition-colors"
              onClick={() => { setIsCreateDialogOpen(false); navigate('/dashboard/agents/new'); }}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" /></svg>
              </div>
              <div>
                <p className="text-sm font-medium">AI Setup</p>
                <p className="text-xs text-muted-foreground">Let AI research your business and build the campaign for you</p>
              </div>
            </div>

            {/* Manual setup */}
            <div className="relative my-3 flex items-center">
              <div className="flex-grow border-t border-border"></div>
              <span className="flex-shrink mx-3 text-xs text-muted-foreground">or</span>
              <div className="flex-grow border-t border-border"></div>
            </div>

            <form onSubmit={handleCreateCampaign} className="space-y-4">
              <div>
                <Input
                  id="campaign-name"
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e.target.value)}
                  placeholder="required"
                  inlineLabel="Name"
                  className="w-full mt-2"
                  autoFocus
                  required
                />
              </div>
              <div className="flex gap-3 pt-4 justify-end -mr-2 -mb-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsCreateDialogOpen(false)}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating || !newCampaignName.trim()}
                  className="flex items-center gap-2"
                  variant="primary"
                >
                  {isCreating ? (
                    <div className="flex items-center gap-2 w-full">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-1 flex-1" />
                    </div>
                  ) : (
                    'Manual Setup'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="w-full">
        {isLoading ? (
          <>
            <div className="relative">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <div className="flex items-center h-full">
                        <Skeleton className="h-5 w-5 rounded-full ml-2" />
                      </div>
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-12" />
                    </TableHead>
                    <TableHead>
                      <Skeleton className="h-4 w-12" />
                    </TableHead>
                    <TableHead className="text-right">
                      <Skeleton className="h-4 w-8 ml-auto" />
                    </TableHead>
                    <TableHead className="text-right">
                      <Skeleton className="h-4 w-8 ml-auto" />
                    </TableHead>
                    <TableHead className="text-right">
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableHead>
                    <TableHead className="text-right">
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableHead>
                    <TableHead className="text-right">
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableHead>
                    <TableHead>
                      <div className="flex justify-end mr-4">
                        <Skeleton className="h-4 w-12" />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center h-full">
                          <Skeleton className="h-5 w-5 rounded-full ml-2" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-normal">
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-11" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-8 ml-auto" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-8 ml-auto" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-8 ml-auto" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-8 ml-auto" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-8 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <Skeleton className="h-8 w-8 mr-4" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4 ml-8">
              <div className="space-x-2">
                <Skeleton className="h-9 w-20 inline-block" />
                <Skeleton className="h-9 w-16 inline-block" />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="relative" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-row-id={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className={`cursor-pointer`}
                        onClick={(e) => {
                          // Don't navigate if clicking on interactive elements
                          if (
                            e.target instanceof Element &&
                            (e.target.closest('button') ||
                             e.target.closest('input') ||
                             e.target.closest('[role="button"]') ||
                             e.target.closest('[data-radix-collection-item]'))
                          ) {
                            return;
                          }
                          navigate(`/dashboard/campaigns/${row.original.id}/edit`);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No campaigns found. Create your first campaign to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Selection Action Bar */}
            <SelectionActionBar
              selectedCount={table.getFilteredSelectedRowModel().rows.length}
              isVisible={table.getFilteredSelectedRowModel().rows.length > 0}
              onClearSelection={() => setRowSelection({})}
              itemName={{ singular: 'campaign', plural: 'campaigns' }}
              tableContainerRef={tableContainerRef}
              selectedRowIds={table.getFilteredSelectedRowModel().rows.map(row => row.id)}
            >
              <Button 
                variant="destructive" 
                size="xs"
                onClick={handleOpenDeleteDialog}
              >
                Delete
              </Button>
            </SelectionActionBar>
            
            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Delete Campaigns</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete {selectedCampaignIds.length} campaign{selectedCampaignIds.length > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() => setIsDeleteDialogOpen(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <TableFooter>
              <span className="text-sm text-foreground whitespace-nowrap">
                <span className="font-bold">{table.getFilteredRowModel().rows.length}</span> campaign{table.getFilteredRowModel().rows.length !== 1 ? 's' : ''}{table.getFilteredRowModel().rows.length > 0 && ` · showing ${table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}–${Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)}`}
              </span>
              {table.getPageCount() > 1 && (
                <div className="space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    Next
                  </Button>
                </div>
              )}
            </TableFooter>
          </>
        )}
      </div>
    </>
  );
}
